import React, { Component } from 'react';
import { Dialog, Transition, Menu } from '@headlessui/react';
import { 
  ArrowLeftIcon, 
  ChevronRightIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  ArchiveBoxIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  InformationCircleIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/apiService';
import outlookService from '../../services/outlookService';
import storageService from '../../services/storageService';
import authService from '../../services/authService';
import LoadingSpinner from '../common/LoadingSpinner';
import ConfirmationNotification from '../common/ConfirmationNotification';
import EntityDetail from './EntityDetail';
import Activities from './Activities';
import AddCommentForm from './AddCommentForm';
import EntityForm from '../forms/EntityForm';
import ShowHeadFields from './ShowHeadFields';
import { renderIcon } from '../utils/Common';
import { toast } from 'react-toastify';

class EditMode extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showForm: false,
      showDetail: false,
      showActivities: false,
      showComment: false,
      loading: false,
      relatedTabs: [],
      loadingRelatedTabs: true,
      showArchiveDialog: false,
      showLinkEmailDialog: false,
      linkEmailLoading: false,
      enabledModules: {},
      selectedModule: '',
      searchQuery: '',
      searchResults: [],
      searchLoading: false,
      searchTimeout: null,
      showArchiveNotification: false,
      showLinkNotification: false,
      notificationMessage: '',
    };
  }

  componentDidMount() {
    // Check if we should open edit form or detail view directly
    if (this.props.entity_info.editMode) {
      this.setState({ showForm: true });
    } else if (this.props.entity_info.viewMode) {
      this.setState({ showDetail: true });
    }
    
    this.loadRelatedTabs();
    this.loadEnabledModules();
  }

  componentWillUnmount() {
    // Cleanup if needed
  }

  loadRelatedTabs = async () => {
    const { entity_info, onLogout } = this.props;
    
    this.setState({ loadingRelatedTabs: true });
    
    
    try {
      const result = await apiService.getRelatedTabModules(entity_info.module, entity_info.crmid);
      
      if (result.success) {
        const relatedTabsData = result.data.data || [];
        
        this.setState({
          relatedTabs: relatedTabsData,
          loadingRelatedTabs: false,
        });
      } else if (result.needsLogout && onLogout) {
        onLogout();
      } else {
        this.setState({ loadingRelatedTabs: false });
      }
    } catch (error) {
      this.setState({ loadingRelatedTabs: false });
    }
  };

  loadEnabledModules = () => {
    
    // Get enabled modules from authService
    const enabledModules = authService.getEnabledModules();

    this.setState({
      enabledModules: enabledModules,
    });
  };

  handleArchiveEmail = async () => {
    try {
      this.setState({ showArchiveDialog: false, loading: true });

      const user = await storageService.getUser();

      // Validate Office.js context before proceeding
      if (!Office.context || !Office.context.mailbox || !Office.context.mailbox.item) {
        throw new Error('Office.js context not available. Please refresh the add-in.');
      }
      
      // Get complete email data including attachments, CC, BCC, etc.
      const completeEmailData = await outlookService.createCompleteEmailArchiveData(
        this.props.entity_info.crmid,
        user.userid
      );

      // Validate required tokens for attachment processing
      if (completeEmailData.attachments.length > 0 && !completeEmailData.callbackToken) {
        throw new Error('Unable to get access token for attachment processing. Please try again.');
      }

      const result = await apiService.archiveEmail(completeEmailData);

      // Check if recordid exists (either as string or object with recordid property)
      const recordId = result.data?.recordid;
      const hasValidRecordId = recordId && (
        (typeof recordId === 'string' && recordId.length > 0) ||
        (typeof recordId === 'object' && recordId.recordid)
      );

      if (result.success && hasValidRecordId) {
        const attachmentText = completeEmailData.hasAttachments
          ? ` with ${completeEmailData.attachments.length} attachment(s)`
          : '';

        this.setState({
          showArchiveNotification: true,
          notificationMessage: `Email archived successfully${attachmentText}`,
          loading: false
        });
      } else if (result.needsLogout && this.props.onLogout) {
        this.props.onLogout();
      } else {
        this.setState({
          showArchiveNotification: true,
          notificationMessage: 'Failed to archive email. Please try again.',
          loading: false
        });
      }
    } catch (error) {
      this.setState({ loading: false });
    }
  };

  handleLinkEmailWithRecord = async (selectedRecord) => {
    try {
      this.setState({ linkEmailLoading: true });

      const user = await storageService.getUser();

      // Validate Office.js context before proceeding
      if (!Office.context || !Office.context.mailbox || !Office.context.mailbox.item) {
        throw new Error('Office.js context not available. Please refresh the add-in.');
      }
      
      // Get complete email data for linking
      const completeEmailData = await outlookService.createCompleteEmailArchiveData(
        selectedRecord.crmid,
        user.userid
      );

      const result = await apiService.archiveEmail(completeEmailData);

      // Check if recordid exists (either as string or object with recordid property)
      const recordId = result.data?.recordid;
      const hasValidRecordId = recordId && (
        (typeof recordId === 'string' && recordId.length > 0) ||
        (typeof recordId === 'object' && recordId.recordid)
      );

      if (result.success && hasValidRecordId) {
        this.setState({
          showLinkNotification: true,
          notificationMessage: `Email linked successfully with ${selectedRecord.label}`,
          showLinkEmailDialog: false,
          selectedModule: '',
          searchQuery: '',
          searchResults: [],
          linkEmailLoading: false
        });
      } else if (result.needsLogout && this.props.onLogout) {
        this.props.onLogout();
      } else {
        this.setState({
          showLinkNotification: true,
          notificationMessage: 'Failed to link email. Please try again.',
          linkEmailLoading: false
        });
      }
    } catch (error) {
      this.setState({ linkEmailLoading: false });
    }
  };

  handleModuleChange = (e) => {
    const module = e.target.value;
    this.setState({
      selectedModule: module,
      searchQuery: '',
      searchResults: []
    });
  };

  handleSearchInputChange = (e) => {
    const query = e.target.value;
    this.setState({ searchQuery: query });

    // Clear existing timeout
    if (this.state.searchTimeout) {
      clearTimeout(this.state.searchTimeout);
    }

    // Clear results if query is too short
    if (query.length < 2) {
      this.setState({ searchResults: [] });
      return;
    }

    // Set new timeout for search
    if (this.state.selectedModule) {
      const timeout = setTimeout(() => {
        this.performSearch(query);
      }, 300); // 300ms debounce
      this.setState({ searchTimeout: timeout });
    }
  };

  performSearch = async (query) => {
    if (!this.state.selectedModule || !query || query.length < 2) {
      return;
    }

    this.setState({ searchLoading: true });

    try {
      
      const result = await apiService.searchRecords(this.state.selectedModule, query, 'linkemail');

      if (result.success) {
        const searchData = result.data.data || result.data.records || result.data || [];
        
        // Process the search results to ensure they have the right structure
        const processedResults = Array.isArray(searchData) ? searchData.map(record => ({
          ...record,
          crmid: record.id || record.crmid,
          label: record.label || record.name || 'Unnamed Record',
          module: this.state.selectedModule
        })) : [];
        
        this.setState({ searchResults: processedResults });
      } else {
        this.setState({ searchResults: [] });
      }
    } catch (error) {
      this.setState({ searchResults: [] });
    } finally {
      this.setState({ searchLoading: false });
    }
  };

  handleArchiveNotificationComplete = () => {
    this.setState({ showArchiveNotification: false });
  };

  handleLinkNotificationComplete = () => {
    this.setState({ showLinkNotification: false });
  };

  handleRelatedModuleInfo = (relModule) => {
    if (relModule === 'ModComments') {
      this.setState({ 
        showComment: true,
        showDetail: false, // Hide detail view when showing comments
        showForm: false,
        showActivities: false
      });
    } else if (relModule === 'Detail') {
      this.setState({ 
        showDetail: true,
        showComment: false,
        showForm: false,
        showActivities: false
      });
    }
  };

  handleFormCancel = () => {
    // When canceling edit form, show detail view instead of going back to main view
    this.setState({ 
      showForm: false,
      showDetail: true,
      showComment: false,
      showActivities: false
    });
  };

  handleFormSuccess = () => {
    // After successful save, show detail view with updated data
    this.setState({ 
      showForm: false,
      showDetail: true,
      showComment: false,
      showActivities: false
    });
  };
  render() {
    const {
      showForm,
      showDetail,
      showActivities,
      showComment,
      loading,
      relatedTabs,
      loadingRelatedTabs,
      showArchiveDialog,
      showLinkEmailDialog,
      linkEmailLoading,
      enabledModules,
      selectedModule,
      searchQuery,
      searchResults,
      searchLoading,
      showArchiveNotification,
      showLinkNotification,
      notificationMessage,
    } = this.state;

    const { entity_info, onLogout, onBack } = this.props;

    if (loading) {
      return (
        <LoadingSpinner label="Processing request..." variant="fullscreen" />
      );
    }

    // Convert enabled modules to array for dropdown
    const moduleOptions = Object.entries(enabledModules).map(([key, moduleData]) => ({
      value: key,
      label: moduleData.singular_modname || moduleData.display_name || moduleData.label || key
    }));

    return (
      <>
        {/* Success Notifications */}
        <ConfirmationNotification
          show={showArchiveNotification}
          message={notificationMessage}
          type="archive"
          onComplete={this.handleArchiveNotificationComplete}
          duration={2500}
        />
        
        <ConfirmationNotification
          show={showLinkNotification}
          message={notificationMessage}
          type="link"
          onComplete={this.handleLinkNotificationComplete}
          duration={2500}
        />
        
        <div className="space-y-4">
        {/* Archive Email Confirmation Dialog */}
        <Transition appear show={showArchiveDialog} as={React.Fragment}>
          <Dialog as="div" className="relative z-10" onClose={() => this.setState({ showArchiveDialog: false })}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                      </div>
                      <div className="ml-3">
                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          Archive Email
                        </Dialog.Title>
                      </div>
                    </div>
                    <div className="mt-3">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to archive this email? This action will save the email and associate it with the current record.
                      </p>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                      <button
                        type="button"
                        className="btn-outline text-sm"
                        onClick={() => this.setState({ showArchiveDialog: false })}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn-primary text-sm"
                        onClick={this.handleArchiveEmail}
                      >
                        Archive Email
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Link Email Dialog */}
        <Transition appear show={showLinkEmailDialog} as={React.Fragment}>
          <Dialog as="div" className="relative z-10" onClose={() => this.setState({ showLinkEmailDialog: false })}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                      <div className="flex items-center">
                        <LinkIcon className="h-6 w-6 text-primary-600 mr-3" />
                        <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                          Link Email
                        </Dialog.Title>
                      </div>
                      <button
                        onClick={() => this.setState({ showLinkEmailDialog: false })}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6">
                      {/* Module Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Module
                        </label>
                        <select
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white shadow-sm"
                          value={selectedModule}
                          onChange={this.handleModuleChange}
                        >
                          <option value="">Choose a module...</option>
                          {moduleOptions.map((module) => (
                            <option key={module.value} value={module.value}>
                              {module.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Search Input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Search Records
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white shadow-sm"
                            placeholder={selectedModule ? `Search ${selectedModule} records...` : "Select a module first"}
                            value={searchQuery}
                            onChange={this.handleSearchInputChange}
                            disabled={!selectedModule}
                          />
                          <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                            {searchLoading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                            ) : (
                              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          Type at least 2 characters to search
                        </p>
                      </div>

                      {/* Search Results */}
                      <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50">
                        {searchLoading ? (
                          <div className="p-8 text-center bg-white">
                            <div className="flex flex-col items-center space-y-3">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                              <span className="text-sm text-gray-600">Searching records...</span>
                            </div>
                          </div>
                        ) : searchResults.length > 0 ? (
                          <div className="bg-white space-y-2 p-2">
                            {searchResults.map((record, index) => (
                              <button
                                key={index}
                                className="w-full px-3 py-3 text-left hover:bg-blue-50 hover:border-blue-200 transition-all duration-200 focus:outline-none focus:bg-blue-50 border border-gray-200 rounded-lg bg-white shadow-sm mx-1 my-1"
                                onClick={() => this.handleLinkEmailWithRecord(record)}
                                disabled={linkEmailLoading}
                              >
                                <div className="space-y-1.5">
                                  {/* Record Name - Bold and Prominent */}
                                  <div>
                                    <h4 className="text-sm font-bold text-gray-900 leading-tight">
                                      {record.label || record.name}
                                    </h4>
                                  </div>
                                  
                                  {/* Header Fields - Enhanced Display */}
                                  {record.header_fields && record.header_fields.length > 0 && (
                                    <div className="space-y-1">
                                      {record.header_fields.slice(0, 2).map((field, fieldIndex) => (
                                        field.value && field.value.trim() !== '' && (
                                          <div key={fieldIndex} className="flex flex-col">
                                            <span className="text-xs font-semibold text-gray-700 mb-0.5">
                                              {field.fieldlabel}:
                                            </span>
                                            <span className="text-xs text-gray-800 font-medium break-words leading-relaxed">
                                              {field.value}
                                            </span>
                                          </div>
                                        )
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : searchQuery.length >= 2 && selectedModule ? (
                          <div className="p-6 text-center bg-white">
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-gray-600">No records found</p>
                              <p className="text-xs text-gray-500">
                                No records found for "<span className="font-medium">{searchQuery}</span>" in {selectedModule}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="p-6 text-center bg-white">
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-gray-600">
                                {!selectedModule 
                                  ? "Select a module to start searching"
                                  : "Start typing to search records"
                                }
                              </p>
                              <p className="text-xs text-gray-500">
                                {!selectedModule 
                                  ? "Choose a module from the dropdown above"
                                  : "Type at least 2 characters to search"
                                }
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
                      <p className="text-xs text-gray-500">
                        Select a record above to link this email
                      </p>
                      <div className="flex space-x-3">
                        <button
                          type="button"
                          className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                          onClick={() => this.setState({ 
                            showLinkEmailDialog: false,
                            selectedModule: '',
                            searchQuery: '',
                            searchResults: []
                          })}
                          disabled={linkEmailLoading}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {showForm ? (
          <EntityForm
            module={entity_info.module}
            crmid={entity_info.crmid}
            entity_info={entity_info}
            onCancel={this.handleFormCancel}
            onSuccess={this.handleFormSuccess}
            onLogout={onLogout}
            {...this.props}
          />
        ) : showDetail ? (
          <EntityDetail
            module={entity_info.module}
            crmid={entity_info.crmid}
            entity_info={entity_info}
            onCancel={onBack || (() => this.setState({ showDetail: false }))}
            onBack={onBack}
            onLogout={onLogout}
            relatedTabs={relatedTabs}
            onRelatedModuleInfo={this.handleRelatedModuleInfo}
            onEdit={(record) => {
              this.setState({ 
                showDetail: false,
                showForm: true,
                showComment: false,
                showActivities: false
              });
            }}
          />
        ) : showActivities ? (
          <Activities
            module={entity_info.module}
            crmid={entity_info.crmid}
            onCancel={() => this.setState({ showActivities: false })}
            onLogout={onLogout}
          />
        ) : showComment ? (
          <AddCommentForm
            crmid={entity_info.crmid}
            onCancel={() => this.setState({ 
              showComment: false,
              showDetail: true // Return to detail view when canceling comments
            })}
            onLogout={onLogout}
          />
        ) : (
          <div className="space-y-4">
            {/* Record Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
             {/* Back Navigation */}
             {onBack && (
  <div className="mb-4 pb-4 border-b border-gray-200">
    <button
      onClick={onBack}
      className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
    >
      <ArrowLeftIcon className="w-4 h-4 mr-2" />
      Back to Results
    </button>
  </div>
)}
             
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <ShowHeadFields data={entity_info} />
                    </div>
                    
                    {/* Edit Icon */}
                    <div className="ml-4 flex items-center space-x-2">
                      {entity_info.editable && (
                        <button
                          onClick={() => {
                            this.setState({ 
                              showForm: true,
                              showDetail: false,
                              showComment: false,
                              showActivities: false
                            });
                          }}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit Record"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>

            {/* Email Action Buttons */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="space-y-3">
                <button
                  onClick={() => this.setState({ showArchiveDialog: true })}
                  className="w-full flex items-center justify-start px-3 py-2 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 text-sm text-left"
                  style={{ backgroundColor: '#339fcf' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#0075a8'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#339fcf'}
                >
                  <ArchiveBoxIcon className="w-4 h-4 mr-2" />
                  Log Email to {entity_info.label}
                </button>
                
                {!showDetail && (
                  <button
                    onClick={() => this.setState({ showLinkEmailDialog: true })}
                    className="w-full flex items-center justify-start px-3 py-2 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 text-sm text-left"
                    style={{ backgroundColor: '#66b7db' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#339fcf'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#66b7db'}
                  >
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Link Email to Another Record
                  </button>
                )}
              </div>
            </div>

            {/* Related Tabs */}
            {loadingRelatedTabs ? (
              <LoadingSpinner label="Loading related tabs..." />
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-4 py-4 border-b border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900">Related Information</h3>
                </div>
                <div className="divide-y divide-gray-200">
                  {relatedTabs.map((tab, index) => (
                    <button
                      key={index}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                      onClick={() => this.handleRelatedModuleInfo(tab.type)}
                    >
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                          {renderIcon(tab.type)}
                        </div>
                        <span className="ml-2 text-sm font-medium text-gray-900">{tab.label}</span>
                      </div>
                      <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        </div>
      </>
    );
  }
}

export default EditMode;